import { useCallback, useMemo, useRef, useState } from 'react';
import { AWS_WAF_CAPTCHA_API_KEY, ENABLE_WAF_CAPTCHA } from '../config';

const FIVE_MINUTES = 5 * 60 * 1000;
const DEFAULT_ERROR_MESSAGE = 'Captcha verification failed. Please try again.';

const buildCaptchaError = (message, detail) => {
  if (detail?.response?.data?.message) {
    return detail;
  }
  const err = detail instanceof Error ? detail : new Error(message);
  err.response = err.response || { data: {} };
  err.response.data.message = message;
  return err;
};

export default function useWafCaptcha() {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);
  const pendingRef = useRef(null);

  const hide = useCallback(() => {
    setVisible(false);
    pendingRef.current = null;
  }, []);

  const resolvePending = useCallback((value, isError = false, fallbackMessage = DEFAULT_ERROR_MESSAGE) => {
    if (pendingRef.current) {
      const payload = isError ? buildCaptchaError(fallbackMessage, value) : value;
      const { resolve, reject } = pendingRef.current;
      if (isError) {
        reject(payload);
      } else {
        resolve(payload);
      }
    }
    hide();
  }, [hide]);

  const ensureCaptcha = useCallback(() => {
    if (!ENABLE_WAF_CAPTCHA) {
      return Promise.resolve();
    }
    const now = Date.now();
    if (window.__wafLastCaptchaTs && now - window.__wafLastCaptchaTs < FIVE_MINUTES) {
      return Promise.resolve();
    }
    if (!AWS_WAF_CAPTCHA_API_KEY) {
      return Promise.reject(buildCaptchaError('Captcha configuration missing. Please contact support.'));
    }
    if (!window.CaptchaScript || !window.ChallengeScript) {
      return Promise.reject(buildCaptchaError('Captcha failed to load. Refresh the page and try again.'));
    }
    return new Promise((resolve, reject) => {
      pendingRef.current = { resolve, reject };
      setVisible(true);
      setTimeout(() => {
        const el = containerRef.current;
        if (!el) {
          resolvePending(new Error('Captcha container missing'), true, 'Unable to render captcha. Please try again.');
          return;
        }
        el.innerHTML = '';
        try {
          window.CaptchaScript.renderCaptcha(
            el,
            async (voucher) => {
              try {
                await window.ChallengeScript.submitCaptcha(voucher);
                window.__wafLastCaptchaTs = Date.now();
                resolvePending();
              } catch (err) {
                resolvePending(err, true);
              }
            },
            {
              apiKey: AWS_WAF_CAPTCHA_API_KEY,
              skipInitialScreen: true,
              dynamicWidth: true,
              onError: (err) => resolvePending(err, true),
            }
          );
        } catch (err) {
          resolvePending(err, true);
        }
      }, 0);
    });
  }, [resolvePending]);

  const CaptchaOverlay = useMemo(() => () => {
    if (!visible) return null;
    return (
      <div style={overlayStyles}>
        <div style={modalStyles}>
          <h2 style={{ marginTop: 0 }}>Human verification</h2>
          <p style={{ marginBottom: '1rem' }}>
            Complete the CAPTCHA challenge below to continue.
          </p>
          <div ref={containerRef} />
        </div>
      </div>
    );
  }, [visible]);

  return { ensureCaptcha, CaptchaOverlay };
}

const overlayStyles = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

const modalStyles = {
  background: '#fff',
  padding: '1.5rem',
  borderRadius: '8px',
  maxWidth: '420px',
  width: '90%',
  boxShadow: '0 10px 25px rgba(0,0,0,0.35)',
};
